// client/src/providers/AuthProvider.tsx
import { ReactNode, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCredentials, logout, setLoading } from '../store/authSlice';
import { authApi } from '../services/api';
import { RootState } from '../store';
import notificationService from '../services/notificationService';

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoading } = useSelector((state: RootState) => state.auth);

    const checkAuth = useCallback(async () => {
        dispatch(setLoading(true));
        const token = localStorage.getItem('token');
        const publicRoutes = ['/login'];

        if (!token) {
            dispatch(setLoading(false));
            if (!publicRoutes.includes(location.pathname)) {
                navigate('/login', { replace: true });
            }
            return;
        }

        try {
            const { data: user } = await authApi.getUser();
            dispatch(setCredentials({ user, token }));

            // Auto-initialize and subscribe to push notifications on login
            try {
                await notificationService.initialize();
                await notificationService.subscribeToPushNotifications();
            } catch {}

            // Only redirect if we're on a public route
            if (publicRoutes.includes(location.pathname)) {
                navigate('/dashboard', { replace: true });
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            localStorage.removeItem('token');
            // Ensure push is unsubscribed on auth failure
            try { await notificationService.unsubscribeFromPushNotifications(); } catch {}
            dispatch(logout());
            if (!publicRoutes.includes(location.pathname)) {
                navigate('/login', { replace: true });
            }
        } finally {
            dispatch(setLoading(false));
        }
    }, [dispatch, navigate, location.pathname]);

    useEffect(() => {
        checkAuth();
        // On app unload or route change to login, ensure subscription is cleaned up
        const handleUnload = async () => {
            try { await notificationService.unsubscribeFromPushNotifications(); } catch {}
        };
        window.addEventListener('beforeunload', handleUnload);
        return () => {
            window.removeEventListener('beforeunload', handleUnload);
        };
    }, []);

    if (isLoading) {
        return <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>;
    }

    return <>{children}</>;
}